import {
  Text,
  Menu,
  Avatar,
  Stack,
  Group,
  Box,
  Divider,
  ActionIcon,
  Badge,
  Tooltip,
} from "@mantine/core";
import { 
  IconLogout, 
  IconChevronDown, 
  IconUser, 
  IconSettings,
  IconMail 
} from "@tabler/icons-react";
import { useLocalStorage } from "@mantine/hooks";
import { User } from "../common/models";
import { useState } from "react";

export function UserButton() {
  const [, , removeToken] = useLocalStorage<string | undefined>({
    key: "token",
  });
  const [user, , removeUser] = useLocalStorage<User | undefined>({
    key: "user",
  });

  const [opened, setOpened] = useState(false);

  async function logout() {
    removeToken();
    removeUser();
    localStorage.clear();
    window.location.href = "/login";
  }

  const initials = `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase();
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  const bgColor1 = "#FC7A69";
  const bgColor2 = "#221E45";

  return (
    <Menu
      trigger="click"
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      offset={12}
      shadow="xl"
      transitionProps={{ transition: 'pop-top-right', duration: 200 }}
    >
      {/* ─── Avatar Trigger Button ─── */}
      <Menu.Target>
        <Tooltip 
          label={fullName || "User Profile"} 
          position="bottom" 
          withArrow
          transitionProps={{ duration: 200 }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "6px 12px 6px 6px",
              borderRadius: "50px",
              cursor: "pointer",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: opened ? "#f3f4f6" : "transparent",
              border: opened ? "1px solid #e5e7eb" : "1px solid transparent",
            }}
            onMouseEnter={(e) => {
              if (!opened) {
                e.currentTarget.style.backgroundColor = "#f9fafb";
                e.currentTarget.style.border = "1px solid #f3f4f6";
              }
            }}
            onMouseLeave={(e) => {
              if (!opened) {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.border = "1px solid transparent";
              }
            }}
          >
            <Avatar
              size={38}
              radius="xl"
              style={{
                background: `linear-gradient(135deg, ${bgColor1} 0%, ${bgColor2} 100%)`,
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "14px",
                border: "2px solid #ffffff",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
                letterSpacing: "0.5px",
              }}
            >
              {initials}
            </Avatar>

            <ActionIcon
              variant="subtle"
              size="sm"
              style={{
                color: "#6b7280",
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: opened ? "rotate(180deg)" : "rotate(0deg)",
              }}
            >
              <IconChevronDown size={16} stroke={2.5} />
            </ActionIcon>
          </Box>
        </Tooltip>
      </Menu.Target>

      {/* ─── Professional Dropdown Menu ─── */}
      <Menu.Dropdown
        style={{
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)",
          borderRadius: "16px",
          border: "1px solid #e5e7eb",
          width: "320px",
          padding: 0,
          overflow: "hidden",
          backgroundColor: "#ffffff",
        }}
      >
        {/* ─── Profile Header Section ─── */}
        <Box
          p="xl"
          style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
            borderBottom: "1px solid #e5e7eb",
            position: "relative",
          }}
        >
          <Group gap="md" wrap="nowrap" align="center">
            <Box style={{ position: "relative" }}>
              <Avatar
                size={56}
                radius="xl"
                style={{
                  background: `linear-gradient(135deg, ${bgColor1} 0%, ${bgColor2} 100%)`,
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "20px",
                  border: "3px solid #ffffff",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                  flexShrink: 0,
                  letterSpacing: "0.5px",
                }}
              >
                {initials}
              </Avatar>
              <Box
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: "14px",
                  height: "14px",
                  backgroundColor: "#10b981",
                  border: "2px solid #ffffff",
                  borderRadius: "50%",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              />
            </Box>

            <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
              <Text
                size="md"
                fw={600}
                style={{
                  color: "#111827",
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  letterSpacing: "-0.01em",
                }}
              >
                {fullName}
              </Text>
              <Group gap={6} align="center">
                <IconMail size={14} color="#9ca3af" stroke={2} />
                <Text
                  size="xs"
                  style={{
                    color: "#6b7280",
                    lineHeight: 1.4,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user?.email}
                </Text>
              </Group>
              <Badge
                size="sm"
                variant="light"
                color="teal"
                radius="md"
                style={{
                  marginTop: "6px",
                  textTransform: "none",
                  fontWeight: 500,
                  alignSelf: "flex-start",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                }}
                leftSection={
                  <Box
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                    }}
                  />
                }
              >
                Active
              </Badge>
            </Stack>
          </Group>
        </Box>

        {/* ─── Menu Items Section (includes Logout moved up) ─── */}
        <Box p="sm">
          <Menu.Item
            leftSection={
              <Box
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  backgroundColor: "#fef2f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                }}
              >
                <IconLogout size={18} stroke={2} color="#dc2626" />
              </Box>
            }
            onClick={logout}
            style={{
              padding: "12px 14px",
              fontSize: "14px",
              fontWeight: 500,
              color: "#dc2626",
              borderRadius: "10px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#fef2f2";
              const icon = e.currentTarget.querySelector('div > div');
              if (icon instanceof HTMLElement) {
                icon.style.backgroundColor = "#fee2e2";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              const icon = e.currentTarget.querySelector('div > div');
              if (icon instanceof HTMLElement) {
                icon.style.backgroundColor = "#fef2f2";
              }
            }}
          >
            Logout
          </Menu.Item>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
}